import {
  CoachingCaseBrowseRequest,
  CoachingCaseBrowseResult,
  CoachingCaseCard,
} from '@euler/model/coaching';
import { Observable, combineLatest, concat, from, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

export interface CaseSearchPage {
  kind: 'ready';
  cases: CoachingCaseCard[];
  total: number;
  /** The next page: being read, or failed and waiting for a retry. */
  more: 'idle' | 'loading' | 'failed';
}

export type CaseSearchState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | CaseSearchPage;

/** Reads one page of a request: the service's search, or a test's fake. */
export type CaseSearchFetch = (
  request: CoachingCaseBrowseRequest & { offset: number; limit: number },
) => Promise<CoachingCaseBrowseResult>;

/**
 * Searches as the request changes, the latest request winning, a page at a
 * time: each `loadMore$` appends the next page, and is ignored while one is
 * being read or when every case is in.
 */
export function caseSearchStates(
  request$: Observable<CoachingCaseBrowseRequest>,
  /** Each emission searches the current request again. */
  retry$: Observable<unknown>,
  loadMore$: Observable<unknown>,
  pageSize: number,
  fetch: CaseSearchFetch,
  debounceMs = 250,
): Observable<CaseSearchState> {
  return combineLatest([
    request$.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ),
    retry$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([request]) => {
      const page = (offset: number) =>
        from(fetch({ ...request, offset, limit: pageSize }));
      return page(0).pipe(
        switchMap(first => {
          let current: CaseSearchPage = {
            kind: 'ready',
            cases: first.cases,
            total: first.total,
            more: 'idle',
          };
          const next$ = loadMore$.pipe(
            filter(
              () =>
                current.more !== 'loading' &&
                current.cases.length < current.total,
            ),
            exhaustMap(() =>
              concat(
                of<CaseSearchPage>({ ...current, more: 'loading' }),
                page(current.cases.length).pipe(
                  map((result): CaseSearchPage => {
                    // A case that moved pages while reading shows once.
                    const seen = new Set(current.cases.map(x => x.id));
                    return {
                      kind: 'ready',
                      cases: [
                        ...current.cases,
                        ...result.cases.filter(x => !seen.has(x.id)),
                      ],
                      total: result.total,
                      more: 'idle',
                    };
                  }),
                  catchError(error => {
                    console.error('coaching case page failed', error);
                    return of<CaseSearchPage>({ ...current, more: 'failed' });
                  }),
                ),
              ),
            ),
          );
          return concat(of(current), next$).pipe(
            tap(value => (current = value)),
          );
        }),
        catchError(error => {
          console.error('coaching case search failed', error);
          return of({ kind: 'failed' as const });
        }),
        startWith({ kind: 'loading' as const }),
      );
    }),
  );
}
